/**
 * This module handles the refresh behavior when authentication status changes.
 *
 * It follows the Strategy pattern to handle different authentication platforms (native vs web)
 * and the Observer pattern to react to authentication state changes.
 */

import bindAllMethods from '../../../utils/bindAllMethods';
import { IEventService } from '../../../services/eventService';
import { INavigationService } from '../../../services/navigationService';
import { InitialNavigationStateRepository } from './InitialNavigationStateRepository';
import { ITenantHandlerService } from '../../../services/tenantHandler';
import { ISessionService } from '../../../services/session';
import { IAuthStrategy, AuthStrategyFactory } from './strategies';
import {
  ServicePriority,
  IContainer
} from '../../../core/containers/IContainer';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import SessionObserver, {
  SessionEvents
} from '../../../services/session/SessionObserver';

/**
 * Parameters required to initialize the RefreshWhenAuthStatusChanged behavior
 */
export interface IRefreshWhenNativeAuthStatusChangedParams {
  authRefreshContainer: IContainer;
  eventService: IEventService;
  navigationService: INavigationService;
  initialNavigationStateRepository: InitialNavigationStateRepository;
  tenantHandlerService: ITenantHandlerService;
  sessionService: ISessionService;
  refreshScreenOnChange: boolean;
  nativeRefreshServicesOnAuthChange: boolean;
  webUIRefreshOnAuthChange: boolean;
  isNative?: boolean;
}

/**
 * Handles the refresh behavior when authentication status changes
 * Uses Strategy Pattern to delegate authentication handling to platform-specific strategies
 * Follows Dependency Inversion Principle by depending on abstractions (IAuthStrategy) not concrete implementations
 */
export default class RefreshWhenAuthStatusChangedBehavior {
  private authStrategy: IAuthStrategy;
  private eventService: IEventService;
  private authRefreshContainer: IContainer;
  private sessionService: ISessionService;

  /**
   * Creates an instance of RefreshWhenAuthStatusChangedBehavior
   * @param params - Configuration parameters
   */
  constructor(params: IRefreshWhenNativeAuthStatusChangedParams) {
    bindAllMethods(this);

    const { isNative = false } = params;
    this.eventService = params.eventService;
    this.authRefreshContainer = params.authRefreshContainer;
    this.sessionService = params.sessionService;

    // Create the appropriate strategy based on platform
    this.authStrategy = AuthStrategyFactory.createStrategy({
      ...params,
      isNative
    });

    // Register the publishSucessfullyEvent's methods to the container
    this.authRefreshContainer.addInitServices(
      this.publishSucessfullyEvent,
      ServicePriority.MESSAGE,
      'RefreshWhenAuthStatusChangedBehavior'
    );
    this.authRefreshContainer.addRefreshServices(
      this.publishSucessfullyEvent,
      ServicePriority.MESSAGE,
      'RefreshWhenAuthStatusChangedBehavior'
    );
  }

  /**
   * Initializes the behavior by delegating to the selected strategy
   */
  public init = async (): Promise<void> => {
    await this.authStrategy.init();
  };

  /**
   * Handles authentication status changes by delegating to the strategy
   */
  public handleAuthStatusChange = async (): Promise<void> => {
    await this.authStrategy.handleAuthStatusChange();
  };

  /**
   * Publishes an event indicating the client has successfully logged in
   * This can be used to trigger UI updates or other behaviors
   */
  public publishSucessfullyEvent = async (): Promise<void> => {
    /* istanbul ignore next */
    internalLogger?.debug?.d2({
      className: 'RefreshWhenAuthStatusChangedBehavior',
      methodName: 'publishSucessfullyEvent',
      message: 'Publishing clientosLoggedinFinished event.',
      parameters: { isLoggedin: this.sessionService.isLoggedIn() }
    });
    await this.eventService.publish(
      this.eventService.eventNames.clientosLoggedinFinished,
      {
        isLoggedin: this.sessionService.isLoggedIn()
      }
    );

    // Publishing even for internal use only
    await SessionObserver.notify(SessionEvents.LOGGEDIN_FINISHED, '');

    const isLoggedIn = this.sessionService.isLoggedIn();
    const eventName = isLoggedIn
      ? this.eventService.eventNames.shellUserLoggedInEventName
      : this.eventService.eventNames.shellUserLoggedOutEventName;

    await this.eventService.publish(eventName, true);
  };
}
